<template>
  <div>
    <label for="advanced_inheritance">{{ label }}</label>

    <select
      v-if="!value"
      :id="id"
      :value="value"
      @change="handleInitialChoice"
      :disabled="disabled"
    >
      <option disabled="true" :value="''">{{ parseTranslation(NONE) }}</option>

      <option
        v-for="option in initialOptions"
        :key="`${id}-${option.value}-select`"
        :value="option.value"
        >{{ option.label }}</option
      >
    </select>

    <div class="adv-inh-config-container" v-else>
      <div
        class="align-center"
        v-for="(origin, index) in value.origin"
        :key="`${id}-origin-key-${index}`"
      >
        <span class="and-indicator" v-if="!!index">{{ parseTranslation(AND) }}</span>

        <SingleOrigin
          :id="`${id}-origin-${index}`"
          :question_index="question_index"
          :questions_groups_index="questions_groups_index"
          :origin_index="index"
        />
      </div>

      <div class="align-center" v-if="addAsSelection">
        <label :for="`as-${id}`">{{ parseTranslation(AS) }}</label>

        <select :id="`as-${id}`" :value="value.as" @change="handleAsChange">
          <option value="answers">{{ parseTranslation(ANSWERS) }}</option>
          <option value="subQuestions">{{ parseTranslation(SUBQUESTIONS) }}</option>
        </select>
      </div>

      <div class="align-center">
        <label :for="`fail-${id}`">{{ parseTranslation(FAIL_FLOW) }}</label>

        <select :id="`fail-${id}`" :value="value.failFlow" @change="handleFailFlowChange">
          <option value="screened">{{ parseTranslation(SCREENED) }}</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { questionsConfig } from '@/utils/questions'
import { advancedOptionToRenderConfig } from '@/utils/advancedOptions'

import SingleOrigin from './origin.vue'

import { acceptedTypes } from './contants'

import * as translationInput from './translations'
const { default: translations, ...translationKeys } = translationInput

export default {
  name: 'AdvancedInheritance',

  components: {
    SingleOrigin,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    question_index: {
      required: true,
      type: Number,
    },
    questions_groups_index: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      optionConfig: null,
      label: '',
      required: false,
      advancedOption: 'advanced_inheritance',
    }
  },

  computed: {
    ...mapGetters(['questionGroups']),

    question() {
      return this.questionGroups[this.questions_groups_index].questions[
        this.question_index
      ]
    },

    addAsSelection() {
      const { hasAnswer, hasSubQuestion } = questionsConfig[this.question.type]

      return hasAnswer && hasSubQuestion
    },

    value() {
      return (
        this.questionGroups[this.questions_groups_index].questions[this.question_index]
          .advancedOptions[this.advancedOption] || ''
      )
    },

    initialOptions() {
      const priorQuestions = this.questionGroups
        .slice(0, this.questions_groups_index)
        .map(({ questions }) => questions)
        .flat()
        .filter(
          ({ type, advancedOptions }) =>
            acceptedTypes.includes(type) && !advancedOptions.advanced_inheritance,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      const actualGroupQuestions = this.questionGroups[
        this.questions_groups_index
      ].questions
        .filter(
          ({ type, advancedOptions }, index) =>
            index < this.question_index &&
            acceptedTypes.includes(type) &&
            !advancedOptions.advanced_inheritance,
        )
        .map(({ code, qid }) => ({
          label: code,
          value: qid,
        }))

      return [...priorQuestions, ...actualGroupQuestions]
    },

    disabled() {
      const group = this.questionGroups[this.questions_groups_index]

      const { advancedOptions } = group.questions[this.question_index]

      const isDisabled = this?.optionConfig.disableChecker?.(advancedOptions)

      const isReferencedInAnotherAdvancedInheritance = group.questions.find(
        ({ advancedOptions, code }) =>
          code !== this.question.code &&
          advancedOptions[this.advancedOption]?.origin.find(
            ({ qid }) => qid === this.question.qid,
          ),
      )

      return (
        isDisabled ||
        !this.initialOptions.length ||
        !!isReferencedInAnotherAdvancedInheritance
      )
    },
  },

  watch: {
    language() {
      this.label = this.optionConfig.displayText[this.language]
    },

    question() {
      if (!this.value) return

      const { as } = this.value

      if (!this.addAsSelection && as) this.updateValue({ ...this.value, as: null })

      if (this.addAsSelection && !as) this.updateValue({ ...this.value, as: 'answers' })
    },

    value() {
      if (!this.value) return

      if (!this.value.origin.length) this.updateValue(null)
    },
  },

  methods: {
    updateValue(newValue) {
      this.$store.dispatch('changeAdvanced', {
        questionGroupIndex: this.questions_groups_index,
        questionIndex: this.question_index,
        advancedKey: this.advancedOption,
        value: newValue,
      })
    },

    handleInitialChoice({ target: { value } }) {
      const { label: code } = this.initialOptions.find(option => value === option.value)

      const initialStructure = {
        failFlow: 'screened',
        as: this.addAsSelection ? 'answers' : null,
        origin: [
          {
            qid: value,
            code,
            type: 'selected',
            target: 'answers',
            alternative: null,
          },
        ],
      }

      this.updateValue(initialStructure)
    },

    removeSelf() {
      this.updateValue(null)
    },

    handleAsChange({ target: { value } }) {
      this.updateValue({
        ...this.value,
        as: value,
      })
    },

    handleFailFlowChange({ target: { value } }) {
      this.updateValue({
        ...this.value,
        failFlow: value,
      })
    },
  },

  created() {
    this.optionConfig = advancedOptionToRenderConfig[this.advancedOption]
    this.label = this.optionConfig.displayText[this.language]
    this.required = this.optionConfig.required
  },
}
</script>

<style scoped lang="scss">
.align-center {
  display: flex;
  align-items: center;
}

label {
  width: fit-content;
  white-space: nowrap;
  user-select: none;
  color: $gray-10;
  @include tipography(16);

  .light & {
    color: $main;
  }
}

select {
  @include advancedOptionSelect;
  margin-left: 12px;
}

> div {
  display: flex;
  align-items: center;
}

.adv-inh-config-container {
  padding: 12px 16px;
  position: static;

  > div {
    margin-top: 10px;
  }
}

.and-indicator {
  margin-right: 8px;
}
</style>
